import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Plus, UserPlus, Copy, RefreshCw, Power, X, Loader2 } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { Card, PageTitle, formatDate } from "../ui";
import {
  listarUtilizadores, convidarUtilizador, alternarAtivo, resetConvite,
} from "../../lib/adminP1Api";
import { useAuth } from "../AuthContext";

export default function AdminUtilizadoresPage() {
  const { user } = useAuth();
  if (user && user.role !== "admin") {
    return <Navigate to="/admin" replace />;
  }
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ email: "", nome: "", role: "rececionista" });
  const [creating, setCreating] = useState(false);
  const [invite, setInvite] = useState(null); // {email, nome, link, expires_at}

  const carregar = async () => {
    setLoading(true);
    setItems(await listarUtilizadores());
    setLoading(false);
  };
  useEffect(() => { carregar(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const r = await convidarUtilizador(form);
      const link = `${window.location.origin}/criar-password/${r.invite_token}`;
      setInvite({
        email: r.email, nome: r.nome, role: r.role,
        link, expires_at: r.invite_expires_at,
      });
      toast.success("Convite criado.");
      setForm({ email: "", nome: "", role: "rececionista" });
      setModalOpen(false);
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao criar convite.");
    } finally { setCreating(false); }
  };

  const onResetInvite = async (u) => {
    try {
      const r = await resetConvite(u.id);
      const link = `${window.location.origin}/criar-password/${r.invite_token}`;
      setInvite({ email: u.email, nome: u.nome, role: u.role, link, expires_at: r.invite_expires_at });
      toast.success("Novo link gerado.");
      carregar();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro."); }
  };

  const toggle = async (u) => {
    try {
      await alternarAtivo(u.id);
      carregar();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro."); }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiado.");
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const mensagem = invite && `Olá ${invite.nome}, foste adicionado(a) ao sistema da Vet Clinic. Cria a tua password aqui (link válido por 24h): ${invite.link}`;

  const inputCls = "w-full rounded-xl bg-white border-2 border-[#1E5F6E]/10 px-3 py-2.5 text-sm focus:border-[#1E5F6E] focus:outline-none";

  return (
    <div data-testid="admin-utilizadores">
      <div className="flex items-start justify-between gap-3 mb-7 flex-wrap">
        <PageTitle subtitle="Equipa com acesso ao backoffice.">Utilizadores</PageTitle>
        <button onClick={() => setModalOpen(true)} data-testid="btn-convidar" className="inline-flex items-center gap-2 rounded-full bg-[#1E5F6E] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#174E5B]">
          <UserPlus className="h-4 w-4" /> Convidar utilizador
        </button>
      </div>

      <Card className="!p-0 overflow-hidden">
        {loading ? <p className="p-6 text-[#5A5A5A]">A carregar...</p> : (
          <table className="w-full text-sm">
            <thead className="bg-[#FAF7F2] text-xs uppercase tracking-wider text-[#5A5A5A]">
              <tr><th className="text-left py-3 px-4">Nome</th><th className="text-left py-3 px-4">Email</th><th className="text-left py-3 px-4">Role</th><th className="text-left py-3 px-4">Estado</th><th className="text-left py-3 px-4">Criado</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t border-[#1E5F6E]/10" data-testid={`user-${u.id}`}>
                  <td className="py-3 px-4 font-semibold">{u.nome}</td>
                  <td className="py-3 px-4">{u.email}</td>
                  <td className="py-3 px-4 capitalize">{u.role}</td>
                  <td className="py-3 px-4">
                    {u.ativo ? <span className="text-xs font-bold text-emerald-700">Ativo</span>
                      : u.invite_token ? <span className="text-xs font-bold text-amber-700">Convite pendente</span>
                      : <span className="text-xs font-bold text-zinc-500">Inativo</span>}
                  </td>
                  <td className="py-3 px-4 text-xs">{formatDate(u.created_at)}</td>
                  <td className="py-3 px-4 flex gap-1.5 justify-end">
                    {u.invite_token && <button onClick={() => onResetInvite(u)} data-testid={`reinvite-${u.id}`} title="Gerar novo link" className="rounded-full bg-white border border-[#1E5F6E]/15 text-[#1E5F6E] px-2.5 py-1 text-xs font-bold inline-flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Link</button>}
                    <button onClick={() => toggle(u)} data-testid={`toggle-${u.id}`} title={u.ativo ? "Desativar" : "Ativar"} className="rounded-full bg-white border border-[#1E5F6E]/15 text-[#1E5F6E] px-2.5 py-1 text-xs font-bold inline-flex items-center gap-1"><Power className="h-3 w-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Convidar modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setModalOpen(false)} data-testid="convite-modal">
          <form onSubmit={submit} className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-lg">Convidar utilizador</h3>
              <button type="button" onClick={() => setModalOpen(false)} className="h-8 w-8 grid place-items-center rounded-lg bg-[#FAF7F2]"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              <label className="block"><span className="text-sm font-semibold">Nome *</span>
                <input required data-testid="conv-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className={inputCls + " mt-1"} /></label>
              <label className="block"><span className="text-sm font-semibold">Email *</span>
                <input type="email" required data-testid="conv-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls + " mt-1"} /></label>
              <label className="block"><span className="text-sm font-semibold">Função</span>
                <select data-testid="conv-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls + " mt-1"}>
                  <option value="rececionista">Rececionista</option>
                  <option value="admin">Administrador</option>
                </select></label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setModalOpen(false)} className="rounded-full bg-white border-2 border-[#1E5F6E]/15 px-4 py-2 text-sm font-bold text-[#1E5F6E]">Cancelar</button>
              <button type="submit" disabled={creating} data-testid="conv-submit" className="inline-flex items-center gap-2 rounded-full bg-[#1E5F6E] text-white px-4 py-2 text-sm font-bold disabled:opacity-40">{creating && <Loader2 className="h-4 w-4 animate-spin" />} Criar convite</button>
            </div>
          </form>
        </div>
      )}

      {/* Link gerado modal */}
      {invite && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setInvite(null)} data-testid="invite-link-modal">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display font-bold text-lg">Link de convite gerado</h3>
              <button onClick={() => setInvite(null)} className="h-8 w-8 grid place-items-center rounded-lg bg-[#FAF7F2]"><X className="h-4 w-4" /></button>
            </div>
            <p className="text-sm text-[#5A5A5A]">
              Para: <strong className="text-[#1A1A1A]">{invite.nome}</strong> &lt;<span className="text-[#1A1A1A]">{invite.email}</span>&gt; · {invite.role}
            </p>
            <div className="mt-4">
              <label className="text-xs uppercase tracking-widest font-bold text-[#88A096] block mb-1">Link (válido 24h)</label>
              <div className="flex gap-2">
                <input readOnly data-testid="invite-link" value={invite.link} className={inputCls} />
                <button onClick={() => copy(invite.link)} data-testid="copy-link" className="rounded-xl bg-[#1E5F6E] text-white px-3 inline-flex items-center gap-1 text-sm font-bold"><Copy className="h-3.5 w-3.5" /> Copiar</button>
              </div>
            </div>
            <div className="mt-4">
              <label className="text-xs uppercase tracking-widest font-bold text-[#88A096] block mb-1">Mensagem pronta</label>
              <textarea readOnly data-testid="invite-msg" value={mensagem} rows={3} className={inputCls} />
              <button onClick={() => copy(mensagem)} data-testid="copy-msg" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white border-2 border-[#1E5F6E]/15 px-3 py-1.5 text-xs font-bold text-[#1E5F6E]"><Copy className="h-3 w-3" /> Copiar mensagem</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
